// src/pages/WeeklyPolicyTrends.jsx
import React, { useEffect, useState, useContext, useCallback } from "react";
import ReactApexChart from "react-apexcharts";
import { request } from "../../utils/request";
import { ColorModeContext } from "../../index";
import "./dashboard.scss";

const WeeksToShow = 4;

// Utility: Calculate ISO week and year for a given Date
function getISOWeekAndYear(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return { week: weekNo, year: d.getUTCFullYear() };
}

// Utility: Build an array of the last N ISO week/year pairs
function buildLastNWeeks(n) {
  const out = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(now.getDate() - i * 7);
    out.push(getISOWeekAndYear(d));
  }
  return out;
}

const WeeklyPolicyTrends = () => {
  const { mode } = useContext(ColorModeContext);
  const isDark = mode === "dark";

  const [loading, setLoading] = useState(false);
  const [series, setSeries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [fetchedAt, setFetchedAt] = useState(null);

  // Color palettes mapped to complement the global theme, without using signal colors
  const paletteLight = ["#0B2545", "#1F6FEB", "#6299F1", "#9ABEF6", "#C7DBFB", "#6B7A90"];
  const paletteDark = ["#0B2545", "#1F6FEB", "#6299F1", "#9ABEF6", "#C7DBFB", "#9AA7BD"];
  const colors = isDark ? paletteDark : paletteLight;

  const transformToSeries = (rows, weeksList) => {
    const deptSet = new Set();
    const map = new Map();

    for (const r of rows) {
      const dept = (r.department || "Unassigned").toString();
      deptSet.add(dept);
      const key = `${r.year}-${r.week}-${dept}`;
      map.set(key, Number(r.violationCount || 0));
    }

    const departments = Array.from(deptSet).sort();
    const seriesOut = departments.map((dept, idx) => {
      const data = weeksList.map((w) => map.get(`${w.year}-${w.week}-${dept}`) || 0);
      return { name: dept, data, color: colors[idx % colors.length] };
    });

    const categoriesOut = weeksList.map((w) => `W${w.week} ${w.year}`);
    return { seriesOut, categories: categoriesOut };
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const weeksList = buildLastNWeeks(WeeksToShow);
      const path = `analytics/policy-violations-weekly?weeks=${WeeksToShow}`;
      const res = await request(path, { method: "GET" });
      const rows = Array.isArray(res) ? res : [];

      const normalized = rows.map((r) => ({
        department: (r.department || "Unassigned").toString(),
        week: Number(r.week),
        year: Number(r.year),
        violationCount: Number(r.violationCount || 0)
      }));

      const { seriesOut, categories: newCategories } = transformToSeries(normalized, weeksList);
      setSeries(seriesOut);
      setCategories(newCategories);
      setFetchedAt(new Date());
    } catch (err) {
      console.error("WeeklyPolicyTrends fetch error:", err);
      setSeries([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Map ApexCharts properties to the global design tokens
  const chartTextColor = isDark ? "#9AA7BD" : "#6B7A90"; // var(--slate)
  const chartBorderColor = isDark ? "rgba(255, 255, 255, 0.12)" : "#E4E9F0"; // var(--hairline)

  const options = {
    chart: {
      type: "bar",
      stacked: true,
      toolbar: { show: false },
      zoom: { enabled: false },
      foreColor: chartTextColor,
      animations: { enabled: true, easing: "easeinout", speed: 700 },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "45%",
        borderRadius: 4,
      }
    },
    dataLabels: { enabled: false },
    stroke: { show: true, width: 2, colors: ["transparent"] },
    xaxis: { 
      categories, 
      axisBorder: { show: false }, 
      axisTicks: { show: false } 
    },
    yaxis: { title: { text: "Policy Violations" } },
    legend: { position: "top", horizontalAlign: "center" },
    fill: { opacity: 0.9 },
    tooltip: { 
      theme: isDark ? "dark" : "light",
      shared: true, 
      intersect: false, 
      y: { formatter: (val) => `${val} violations` } 
    },
    colors: series.map((s) => s.color).length ? series.map((s) => s.color) : colors,
    grid: {
      borderColor: chartBorderColor,
      strokeDashArray: 4,
    },
  };

  return (
    <div className="widget-card" style={{ width: "100%" }}>
      <div 
        className="global-card"
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          padding: "0.9rem",
          boxSizing: "border-box"
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
          <h5 className="global-section-title" style={{ margin: 0 }}>
            Policy Violation Trends (Last {WeeksToShow} Weeks)
          </h5>
        </div>

        <div style={{ flex: "1 1 auto", display: "flex", flexDirection: "column", minHeight: 280 }}>
          {loading ? (
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div className="text-secondary" style={{ fontWeight: 600 }}>Calculating...</div>
            </div>
          ) : series.length === 0 || categories.length === 0 ? (
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div className="text-secondary">No data for the selected weeks</div>
            </div>
          ) : (
            <div style={{ width: "100%", height: "100%" }}>
              <ReactApexChart options={options} series={series} type="bar" height={320} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WeeklyPolicyTrends;